import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, StatCard, Button, Badge } from '@/backoffice/components/ui';
import { DriverMissionCard } from '@/backoffice/components/driver/DriverMissionCard';
import { DriverGpsTracker } from '@/backoffice/components/driver/DriverGpsTracker';
import { formatCurrency, formatDate, formatTime } from '@/backoffice/utils';
import {
  fetchDriverDashboard,
  fetchDriverMissions,
  acceptDriverMission,
  rejectDriverMission,
  type DriverDashboardData,
  type DriverMission,
} from '@/domains/driver/services/driver.service';
import {
  Users,
  Package,
  Route,
  Wallet,
  Clock,
  Calendar,
  Plus,
  TrendingUp,
  Loader2,
} from 'lucide-react';

export function CommunityDriverDashboard() {
  const [dashboard, setDashboard] = useState<DriverDashboardData | null>(null);
  const [missions, setMissions] = useState<DriverMission[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const load = () => {
    Promise.all([fetchDriverDashboard(), fetchDriverMissions()])
      .then(([dash, list]) => {
        setDashboard(dash);
        setMissions(list);
      })
      .catch(() => {
        setDashboard(null);
        setMissions([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const stats = dashboard?.stats;
  const trips = dashboard?.trips ?? [];
  const pendingMissions = missions.filter((o) => o.status === 'pending').slice(0, 3);

  const handleAccept = async (id: number) => {
    setActionLoading(true);
    try {
      await acceptDriverMission(id);
      load();
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id: number) => {
    setActionLoading(true);
    try {
      await rejectDriverMission(id);
      load();
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="community_driver" title="Tableau de bord">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="community_driver" title="Tableau de bord">
      <DriverGpsTracker />

      <Card className="mb-6 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-orange-200 flex items-center justify-center">
            <Users className="w-6 h-6 text-orange-700" />
          </div>
          <div>
            <h2 className="font-semibold text-orange-900">Bienvenue, {dashboard?.driver_name ?? 'Livreur'} !</h2>
            <p className="text-sm text-orange-700">
              En tant que livreur communautaire, vous aidez vos voisins tout en gagnant de l'argent.
            </p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-700">Gains du jour</p>
              <p className="text-2xl font-bold text-orange-800">{formatCurrency(stats?.today_earnings ?? 0)}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-orange-200 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-orange-700" />
            </div>
          </div>
        </Card>
        <StatCard
          title="Livraisons aujourd'hui"
          value={stats?.today_deliveries ?? 0}
          icon={<Package className="w-6 h-6 text-blue-600" />}
          iconBg="bg-blue-50"
        />
        <StatCard
          title="Trajets actifs"
          value={stats?.active_trips ?? 0}
          icon={<Route className="w-6 h-6 text-purple-600" />}
          iconBg="bg-purple-50"
        />
        <StatCard
          title="Note"
          value={`${stats?.average_rating ?? 0}/5`}
          icon={<TrendingUp className="w-6 h-6 text-amber-600" />}
          iconBg="bg-amber-50"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Route className="w-5 h-5 text-orange-600" />
              Mes trajets
            </h3>
            <Link to="/dashboard/livreur/trajets">
              <Button variant="outline" size="sm" leftIcon={<Plus className="w-4 h-4" />}>
                Nouveau trajet
              </Button>
            </Link>
          </div>

          {trips.length === 0 ? (
            <div className="text-center py-8">
              <Route className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucun trajet planifié</p>
            </div>
          ) : (
            <div className="space-y-3">
              {trips.map((trip) => (
                <div key={trip.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-gray-900">{trip.from.city}</span>
                      <span className="text-gray-400">→</span>
                      <span className="font-medium text-gray-900">{trip.to.city}</span>
                    </div>
                    <Badge variant={trip.status === 'planned' ? 'primary' : 'success'}>
                      {trip.status === 'planned' ? 'Planifié' : trip.status === 'in_progress' ? 'En cours' : 'Terminé'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(trip.departure_date)} à {formatTime(trip.departure_date)}
                    </span>
                    <span className="text-emerald-600 font-medium">{trip.available_space} kg restant</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Missions proposées
            </h3>
            <Badge variant="warning">{pendingMissions.length}</Badge>
          </div>

          {pendingMissions.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune mission en attente</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingMissions.map((mission) => (
                <DriverMissionCard
                  key={mission.id}
                  mission={mission}
                  compact
                  actionLoading={actionLoading}
                  onAccept={() => handleAccept(mission.id)}
                  onReject={() => handleReject(mission.id)}
                />
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="mt-6">
        <h3 className="font-semibold text-gray-900 mb-4">Avantages du livreur communautaire</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-1">Flexibilité totale</h4>
            <p className="text-sm text-blue-700">Travaillez quand vous voulez, selon vos trajets personnels.</p>
          </div>
          <div className="p-4 bg-emerald-50 rounded-lg">
            <h4 className="font-medium text-orange-900 mb-1">Pas de documents complexes</h4>
            <p className="text-sm text-orange-700">Seule une pièce d'identité est requise pour commencer.</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <h4 className="font-medium text-purple-900 mb-1">Revenus supplémentaires</h4>
            <p className="text-sm text-purple-700">Gagnez de l'argent facilement pendant vos déplacements.</p>
          </div>
        </div>
      </Card>
    </DashboardLayout>
  );
}
